// "Working updates · weekly" column for the dedicated full project page.
// - Week + reported-track count come from the server-discovered current week.
// - Project summary this week: canonical weekly_updates narrative, own editor,
//   gated by server capability canSummary (owner / Delivery Director / admin).
// - Track posting: only exact assigned tracks that the server also lists in
//   capabilities.canSetTrackStatus. Ownership or role alone never grants it.
// - Carry-over: prior frozen week's captured project meeting notes.
import { useEffect, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getMyAssignments, getWeeklyOverview, putWeeklySummary, OVERALL_STATUSES } from "../../../data/projectWeeklyClient.js";
import { TRACKS, TRACK_STATUS_META, OVERALL_STATUS_META, statusForTrack, resolveLine, weekLabel, fmtIsoDate, trackLabel, weeklyTrackState, assignedTrackCompleteness } from "../weekly/weeklyModel.js";
import { weeklyText } from "../../../lib/weeklyRichText.js";
import { useCurrentProjectWeek, RecentProjectWeeks } from "../weekly/ProjectWorkingUpdates.jsx";
import { TrackCompleteness, TrackDot } from "../weekly/TrackCompleteness.jsx";
import { LineView, lineInWeek } from "../weekly/ProjectWorkingUpdatesView.jsx";
import { groupAssignments } from "../weekly/myUpdateModel.js";
import { InfoTip } from "./InfoTip.jsx";

export const WEEKLY_TIP = "What changed this week. Many contributors post on their own track; feeds the Weekly Review and freezes into the archive.";

const isBlank = (s) => typeof s !== "string" || !s.trim();

/** Tracks the caller may post on: exact assignment AND server capability, on a writable week. */
export function postableTracks(entry, capabilities, writable) {
  if (!writable || !entry) return [];
  const allowed = new Set(Array.isArray(capabilities?.canSetTrackStatus) ? capabilities.canSetTrackStatus : []);
  return TRACKS.map((t) => t.key).filter((k) => entry.trackKeys.includes(k) && allowed.has(k));
}

/** Summary edit: server capability only (owner / Delivery Director / admin), never contributor assignment. */
export function canEditWeeklySummary(capabilities, writable) {
  return Boolean(writable && capabilities?.canSummary);
}

export function postNote(mine) {
  const others = TRACKS.filter((t) => !mine.includes(t.key)).map((t) => t.label);
  const mineText = mine.map(trackLabel).join(", ");
  return `You can post on the tracks you're assigned to (${mineText}).${others.length ? ` ${others.join(", ")} ${others.length === 1 ? "is" : "are"} posted by their assigned contributors.` : ""}`;
}

const sx = {
  card: { background: T.card, border: `1px solid ${T.projectsWeeklyBorder}`, borderRadius: 12, minWidth: 0 },
  head: { background: T.projectsWeeklySurface, borderBottom: `1px solid ${T.projectsWeeklyBorder}`, borderRadius: "12px 12px 0 0", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  title: { fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubLive, display: "flex", alignItems: "center" },
  btn: { border: `1px solid ${T.projectsWeeklyBorder}`, background: T.card, borderRadius: 8, padding: "6px 12px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.projectsHubLive, cursor: "pointer" },
  body: { padding: "16px 18px" },
  wk: { fontSize: T.fsSm, fontWeight: T.fwBold, color: T.projectsHubLive, marginBottom: 12 },
  lab: { fontSize: T.fsXxs5, fontWeight: T.fwBold, letterSpacing: ".04em", textTransform: "uppercase", color: T.mid },
  panel: { fontSize: T.fsSm, color: T.mid, background: T.hoverBg, borderRadius: 8, padding: "10px 12px" },
  err: { fontSize: T.fsSm, color: T.red, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: "10px 12px" },
  retry: { marginLeft: 8, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.red, background: "none", border: `1px solid ${T.alertErrBorder}`, borderRadius: 6, padding: "2px 8px", cursor: "pointer" },
  input: { width: "100%", boxSizing: "border-box", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", fontSize: T.fsSm, color: T.dark, background: T.white, fontFamily: "inherit" },
  mine: { fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.projectsHubLive, background: T.projectsWeeklySurface, border: `1px solid ${T.projectsWeeklyBorder}`, borderRadius: 5, padding: "0 5px", marginLeft: 6, verticalAlign: "middle", whiteSpace: "nowrap" },
};

function SummaryBlock({ project, weekDate, editable, onSaved }) {
  const summary = project.summary || null;
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState("");
  const [narrative, setNarrative] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const start = () => { setStatus(summary?.statusOverall || ""); setNarrative(summary?.narrative || ""); setError(null); setEditing(true); };
  const save = async () => {
    if (!OVERALL_STATUSES.includes(status)) { setError("Choose an overall status for this week."); return; }
    setSaving(true); setError(null);
    try {
      await putWeeklySummary(project.id, { weekDate, statusOverall: status, narrative });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err?.message || "Could not save the summary.");
    } finally { setSaving(false); }
  };
  const owner = isBlank(project.owner) || project.owner === "Not assigned" ? "Owner not assigned" : `Owner · ${project.owner}`;
  const overall = summary ? OVERALL_STATUS_META[summary.statusOverall] : null;
  return (
    <div data-testid="full-weekly-summary" style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, background: T.card }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7, flexWrap: "wrap" }}>
        <span style={sx.lab}>Project summary this week</span>
        <span data-testid="full-weekly-summary-owner" style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.projectsHubNavy, background: T.lightGray, borderRadius: 6, padding: "2px 8px" }}>{owner}</span>
        {overall && <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: overall.color, background: overall.bg, borderRadius: 999, padding: "1px 8px" }}>{overall.label}</span>}
        {editable && !editing && <button type="button" data-testid="full-weekly-summary-edit" onClick={start} style={{ ...sx.btn, marginLeft: "auto", padding: "4px 10px", color: T.projectsHubNavy, borderColor: T.border }}>Edit</button>}
      </div>
      {!editing && (
        <div data-testid="full-weekly-summary-text" style={{ fontSize: T.fsSm, color: summary?.narrative ? T.dark : T.mid, fontStyle: summary?.narrative ? "normal" : "italic", whiteSpace: "pre-wrap" }}>
          {weeklyText(summary?.narrative) || "No project summary for this week yet."}
        </div>
      )}
      {editing && (
        <div data-testid="full-weekly-summary-form">
          <label style={{ display: "block", marginBottom: 6 }}>
            <span style={{ ...sx.lab, display: "block", marginBottom: 3 }}>Overall status</span>
            <select aria-label="Overall weekly status" data-testid="full-weekly-summary-status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...sx.input, width: "auto" }}>
              <option value="">Choose...</option>
              {OVERALL_STATUSES.map((s) => <option key={s} value={s}>{OVERALL_STATUS_META[s]?.label || s}</option>)}
            </select>
          </label>
          <textarea aria-label="Project summary this week" data-testid="full-weekly-summary-input" rows={4} value={weeklyText(narrative)} onChange={(e) => setNarrative(e.target.value)} style={{ ...sx.input, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button type="button" data-testid="full-weekly-summary-save" disabled={saving} onClick={save} style={{ ...sx.btn, background: T.projectsHubLive, color: T.white, borderColor: T.projectsHubLive, opacity: saving ? 0.65 : 1 }}>{saving ? "Saving..." : "Save summary"}</button>
            <button type="button" disabled={saving} onClick={() => { setEditing(false); setError(null); }} style={{ ...sx.btn, color: T.mid, borderColor: T.border }}>Cancel</button>
          </div>
          {error && <div role="alert" data-testid="full-weekly-summary-error" style={{ ...sx.err, marginTop: 8, fontSize: T.fsXs }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

function TrackRow({ track, project, lines, mine, unassigned }) {
  const ts = weeklyTrackState(project, track.key);
  const st = statusForTrack(project, track.key);
  const meta = (st && TRACK_STATUS_META[st.status]) || { label: "No status", color: T.light };
  return (
    <div data-testid={`full-track-${track.key}`} style={{ display: "grid", gridTemplateColumns: "12px minmax(80px, 105px) 1fr", gap: 9, alignItems: "baseline", padding: "9px 0", borderBottom: `1px solid ${T.lightGray}` }}>
      <TrackDot row={ts} testId={`full-track-dot-${track.key}`} />
      <div style={{ fontWeight: T.fwBold, color: T.projectsHubNavy, fontSize: T.fsSm }}>
        {track.label}
        {unassigned && <span style={{ ...sx.mine, color: T.mid, background: T.lightGray, borderColor: T.border }} data-testid={`full-track-unassigned-${track.key}`}>Not assigned</span>}
        {mine && <span style={sx.mine} data-testid={`full-track-mine-${track.key}`}>Your track</span>}
      </div>
      <div style={{ minWidth: 0, marginLeft: -18 }}>
        {lines.length === 0
          ? <div style={{ marginLeft: 18, fontSize: T.fsSm, color: T.mid }}>{ts.state === "quiet" ? "Nothing this week (quiet)." : st ? `Status set (${meta.label}); no update text.` : "No update text."}</div>
          : lines.map((l) => <LineView key={l.id} line={l} />)}
      </div>
    </div>
  );
}

function CarryOver({ projectId, currentWeek, weeks, cache }) {
  const prior = (weeks || []).filter((w) => w.frozen && w.weekDate < currentWeek).map((w) => w.weekDate).sort().reverse()[0] || null;
  const [state, setState] = useState({ loading: Boolean(prior), notes: [], legacy: false, member: false, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!prior) return undefined;
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    if (!cache.has(prior)) {
      const pending = Promise.resolve().then(() => getWeeklyOverview(prior)).then((data) => {
        if (!data?.frozen || data.weekDate !== prior || !Array.isArray(data.projects)) throw new Error("Frozen archive content is unavailable.");
        return data;
      }).catch((err) => { cache.delete(prior); throw err; });
      cache.set(prior, pending);
    }
    cache.get(prior).then((data) => {
      if (!active) return;
      const p = data.projects.find((x) => String(x.id) === String(projectId));
      setState({ loading: false, legacy: Boolean(data.legacyUnavailable), member: Boolean(p), notes: (p?.meetingNotes || []).filter((n) => !isBlank(n.note)), error: null });
    }).catch((err) => { if (active) setState({ loading: false, notes: [], legacy: false, member: false, error: err?.message || "Could not load the prior meeting." }); });
    return () => { active = false; };
  }, [prior, projectId, cache, attempt]);
  const box = { background: T.utilAmber, border: `1px solid ${T.alertWarnBorder || T.border}`, borderRadius: 9, padding: "10px 13px", fontSize: T.fsSm, color: T.amberDark, margin: "14px 0 12px" };
  if (!prior) return <div data-testid="full-carry-over" style={{ ...sx.panel, margin: "14px 0 12px" }}>No prior meeting has been frozen yet, so there is no carry-over.</div>;
  const label = fmtIsoDate(prior);
  return (
    <div data-testid="full-carry-over" style={box}>
      <b style={{ color: T.dark }}>From last meeting ({label}):</b>{" "}
      {state.loading && <span>Loading captured notes...</span>}
      {state.error && <span role="alert">{state.error} <button type="button" onClick={() => setAttempt((n) => n + 1)} style={{ ...sx.retry, color: T.amberDark }}>Retry</button></span>}
      {!state.loading && !state.error && state.legacy && <span>Meeting notes were not captured for that week.</span>}
      {!state.loading && !state.error && !state.legacy && !state.member && <span>This project was not part of that meeting.</span>}
      {!state.loading && !state.error && !state.legacy && state.member && state.notes.length === 0 && <span>No meeting notes were captured for this project.</span>}
      {!state.loading && !state.error && state.notes.length > 0 && (
        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
          {state.notes.map((n, i) => <li key={n.id ?? i} data-testid={`full-carry-note-${n.id ?? i}`}>{n.note}{n.authorName ? <span style={{ color: T.mid }}> - {n.authorName}</span> : null}</li>)}
        </ul>
      )}
    </div>
  );
}

export function WeeklyColumn({ projectId, refreshToken, onRefresh, onPost, archiveCache, onOpenArchive, assignmentsLoader = getMyAssignments, canRequestAssignments = true }) {
  const [state, retry] = useCurrentProjectWeek(projectId, refreshToken);
  const localCache = useRef(new Map());
  const cache = archiveCache || localCache.current;
  const [assign, setAssign] = useState({ loading: true, rows: [], error: null });
  // Assignments change elsewhere (People & roles); recheck on focus and periodically.
  const [assignTick, setAssignTick] = useState(0);
  useEffect(() => {
    const tick = () => setAssignTick((n) => n + 1);
    const timer = window.setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); };
  }, []);
  useEffect(() => {
    if (!canRequestAssignments) { setAssign({ loading: false, rows: [], error: null }); return undefined; }
    const controller = new AbortController();
    setAssign((a) => ({ ...a, loading: a.rows.length === 0 && !a.error ? true : a.loading, error: null }));
    Promise.resolve().then(() => assignmentsLoader({ signal: controller.signal }))
      .then((rows) => setAssign({ loading: false, rows: Array.isArray(rows) ? rows : [], error: null }))
      .catch((err) => { if (err?.name !== "AbortError") setAssign({ loading: false, rows: [], error: err?.message || "Could not load your assignments." }); });
    return () => controller.abort();
  }, [projectId, refreshToken, canRequestAssignments, assignmentsLoader, assignTick]);

  const { loading, error, data } = state;
  const project = data ? (data.projects || []).find((p) => String(p.id) === String(projectId)) || null : null;
  const writable = Boolean(data && !data.frozen && !data.historical && data.weekDate === data.currentWeek);
  const entry = groupAssignments(assign.rows).find((e) => String(e.projectId) === String(projectId)) || null;
  const myTracks = entry?.trackKeys || [];
  const postable = project ? postableTracks(entry, project.capabilities, writable) : [];
  const canPost = postable.length > 0 && Boolean(onPost);

  let body;
  if (loading) {
    body = <div data-testid="full-weekly-loading">{[70, 90, 55, 80].map((w) => <div key={w} style={{ height: 14, width: `${w}%`, borderRadius: 6, background: T.lightGray, margin: "10px 0" }} />)}</div>;
  } else if (error) {
    body = <div role="alert" data-testid="full-weekly-error" style={sx.err}>{error}<button type="button" style={sx.retry} onClick={retry} data-testid="full-weekly-retry">Retry</button></div>;
  } else if (data?.legacyUnavailable) {
    body = <div style={sx.panel}>{weekLabel(data.weekDate)} was archived before per-track updates were recorded.</div>;
  } else if (!project) {
    body = <div style={sx.panel} data-testid="full-weekly-not-in-week">This project is not part of the Weekly Review for {weekLabel(data?.weekDate).replace(/^Week/, "the week")}.</div>;
  } else {
    const comp = assignedTrackCompleteness(project);
    const assignedKeys = new Set(comp.available ? project.assignedTrackKeys : []);
    const lines = (project.lines || []).filter((l) => lineInWeek(l, data.weekDate) && (!isBlank(l.note) || !isBlank(resolveLine(l).current)));
    const shown = TRACKS.filter((t) => lines.some((l) => l.trackKey === t.key) || statusForTrack(project, t.key) || weeklyTrackState(project, t.key).state === "quiet");
    const missing = comp.available ? TRACKS.filter((t) => assignedKeys.has(t.key) && !shown.includes(t)) : [];
    body = (
      <>
        <div style={sx.wk} data-testid="full-weekly-week">
          {weekLabel(data.weekDate)}{data.frozen ? " · Frozen · read-only" : ""}
        </div>
        <div style={{ marginTop: -6, marginBottom: 12 }}><TrackCompleteness project={project} surface="full" /></div>
        <SummaryBlock key={`${data.weekDate}-${project.summary?.id ?? "none"}-${project.summary?.narrative ?? ""}`} project={project} weekDate={data.weekDate} editable={canEditWeeklySummary(project.capabilities, writable)} onSaved={onRefresh} />
        <div style={{ ...sx.lab, marginBottom: 6 }}>Track updates</div>
        {shown.length === 0 && <div style={sx.panel} data-testid="full-weekly-empty">No track updates posted this week.</div>}
        {shown.map((t) => <TrackRow key={t.key} track={t} project={project} lines={lines.filter((l) => l.trackKey === t.key)} mine={myTracks.includes(t.key)} unassigned={comp.available && !assignedKeys.has(t.key)} />)}
        {missing.length > 0 && <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 8 }} data-testid="full-weekly-missing">Not reported yet: {missing.map((t) => t.label).join(", ")}.</div>}
        <div style={{ margin: "14px 0 6px" }}>
          {canPost && (
            <button type="button" data-testid="full-post-my-update" onClick={() => onPost(projectId)} style={{ width: "100%", padding: 11, borderRadius: 9, border: "none", background: T.projectsHubLive, color: T.white, fontWeight: T.fwBold, fontSize: T.fsBase, cursor: "pointer" }}>
              + Post my update - {postable.map(trackLabel).join(", ")}{postable.length === 1 ? " track" : " tracks"}
            </button>
          )}
          <div data-testid="full-post-note" style={{ fontSize: T.fsXs, color: T.mid, textAlign: "center", marginTop: 6 }}>
            {assign.loading ? "Checking your track assignments..."
              : assign.error ? <span role="alert" style={{ color: T.red }}>{assign.error}</span>
              : !writable ? "This week is read-only."
              : canPost ? postNote(postable)
              : myTracks.length ? `You are assigned to ${myTracks.map(trackLabel).join(", ")}, but posting is not open to you for this week.`
              : "Track updates are posted by each track's assigned contributors. You are not assigned to a track on this project."}
          </div>
        </div>
      </>
    );
  }

  return (
    <section data-testid="full-weekly-card" style={sx.card} aria-labelledby="full-weekly-title">
      <div style={sx.head}>
        <h2 id="full-weekly-title" style={{ ...sx.title, margin: 0 }}>
          Working updates · weekly
          <InfoTip text={WEEKLY_TIP} label="About working updates" testId="full-weekly-info" />
        </h2>
        {canPost && <button type="button" style={sx.btn} onClick={() => onPost(projectId)} data-testid="full-post-update-header">Post update</button>}
      </div>
      <div style={sx.body}>
        {body}
        {data && !error && <CarryOver projectId={projectId} currentWeek={data.currentWeek} weeks={state.weeks} cache={cache} />}
        {data && !error && <RecentProjectWeeks key={projectId} projectId={projectId} currentWeek={data.currentWeek} weeks={state.weeks} cache={cache} onOpenArchive={onOpenArchive} />}
        <div data-testid="full-weekly-footer" style={{ fontSize: T.fsXs, color: T.mid, borderTop: `1px solid ${T.border}`, paddingTop: 11, marginTop: 12 }}>
          These updates appear in the <b style={{ color: T.dark }}>Weekly Review</b> and freeze into the weekly archive.
        </div>
      </div>
    </section>
  );
}
